import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { useChangePassword, usePasswordPolicy } from "@/containers/Login/hooks/useAuth";

const ChangePasswordForm: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const changePassword = useChangePassword();
  const { data: policy } = usePasswordPolicy();

  const rules = useMemo(() => {
    const p = policy ?? {
      min_length: 12,
      require_uppercase: true,
      require_lowercase: true,
      require_number: true,
      require_special: true,
    };
    const r: { label: string; test: (pw: string) => boolean }[] = [
      { label: `At least ${p.min_length} characters`, test: (pw) => pw.length >= p.min_length },
    ];
    if (p.require_uppercase) r.push({ label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) });
    if (p.require_lowercase) r.push({ label: "One lowercase letter", test: (pw) => /[a-z]/.test(pw) });
    if (p.require_number) r.push({ label: "One number", test: (pw) => /[0-9]/.test(pw) });
    if (p.require_special) r.push({ label: "One special character (!@#$%^&*)", test: (pw) => /[!@#$%^&*]/.test(pw) });
    return r;
  }, [policy]);

  const passwordErrors = useMemo(
    () => rules.filter((rule) => !rule.test(newPassword)),
    [newPassword, rules],
  );

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isPasswordValid = newPassword.length > 0 && passwordErrors.length === 0;
  const isFormValid = currentPassword.length > 0 && isPasswordValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await changePassword
      .mutateAsync({ current_password: currentPassword, new_password: newPassword })
      .then(() => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch(() => {});
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        Update your account password. You will need to enter your current
        password for verification.
      </Typography>

      {changePassword.error && (
        <Alert severity="error">
          {changePassword.error.message || "Password change failed. Please try again."}
        </Alert>
      )}

      {changePassword.isSuccess && (
        <Alert severity="success">Password updated successfully.</Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={changePassword.isPending}
            size="small"
          />
          <TextField
            required
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={changePassword.isPending}
            size="small"
          />

          {newPassword.length > 0 && (
            <Stack spacing={0.5}>
              {rules.map((rule) => {
                const passed = rule.test(newPassword);
                return (
                  <Box key={rule.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {passed ? (
                      <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
                    ) : (
                      <CloseIcon sx={{ fontSize: 16, color: "error.main" }} />
                    )}
                    <Typography variant="caption" color={passed ? "success.main" : "error.main"}>
                      {rule.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}

          <TextField
            required
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={confirmPassword.length > 0 && !passwordsMatch ? "Passwords do not match" : " "}
            disabled={changePassword.isPending}
            size="small"
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || changePassword.isPending}
            >
              {changePassword.isPending ? <CircularProgress size={24} color="inherit" /> : "Update Password"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ChangePasswordForm;
